import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, TOKEN_KEY } from "../lib/api";

const AuthContext = createContext(null);
const ADMIN_TOKEN_KEY = "force_admin_token";

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [ready, setReady] = useState(false);
    const [impersonating, setImpersonating] = useState(false);
    // Raw JWT kept in React state (mirrors localStorage) so subscribers like
    // CallOverlay re-connect their WebSocket on login/logout/impersonation.
    const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

    useEffect(() => {
        const stored = localStorage.getItem(TOKEN_KEY);
        setImpersonating(!!localStorage.getItem(ADMIN_TOKEN_KEY));
        if (!stored) {
            setReady(true);
            return;
        }
        // Sliding session: swap the stored token for a fresh one on every launch
        // (tokens expire after 30 days of *inactivity*, not 30 days flat).
        // Never rotate while impersonating — the admin token must stay intact.
        const impersonatingNow = !!localStorage.getItem(ADMIN_TOKEN_KEY);
        const boot = impersonatingNow
            ? api.get("/auth/me")
            : api.post("/auth/refresh").then((res) => {
                if (res.data?.token) { localStorage.setItem(TOKEN_KEY, res.data.token); setToken(res.data.token); }
                return res;
            });
        boot
            .then((res) => setUser(res.data.user))
            .catch((err) => {
                // Only a rejected token means "signed out"; a network blip keeps the session.
                if (err?.response?.status === 401) { localStorage.removeItem(TOKEN_KEY); setToken(null); }
            })
            .finally(() => setReady(true));
    }, []);

    const login = useCallback((newToken, userData) => {
        localStorage.setItem(TOKEN_KEY, newToken);
        sessionStorage.removeItem("force_view"); // fresh login always starts on the map-home
        setToken(newToken);
        setUser(userData);
    }, []);

    const refreshUser = useCallback(async () => {
        try {
            const res = await api.get("/auth/me");
            setUser(res.data.user);
        } catch (e) {
            /* ignore */
        }
    }, []);

    const logout = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        sessionStorage.removeItem("force_view");
        setImpersonating(false);
        setToken(null);
        setUser(null);
    }, []);

    // Admin -> become another user. Stashes the admin token so it can be restored.
    const impersonate = useCallback((newToken, userData) => {
        const current = localStorage.getItem(TOKEN_KEY);
        if (current && !localStorage.getItem(ADMIN_TOKEN_KEY)) {
            localStorage.setItem(ADMIN_TOKEN_KEY, current);
        }
        localStorage.setItem(TOKEN_KEY, newToken);
        setToken(newToken);
        setUser(userData);
        setImpersonating(true);
    }, []);

    const stopImpersonate = useCallback(async () => {
        const adminToken = localStorage.getItem(ADMIN_TOKEN_KEY);
        if (!adminToken) return;
        localStorage.setItem(TOKEN_KEY, adminToken);
        localStorage.removeItem(ADMIN_TOKEN_KEY);
        setToken(adminToken);
        setImpersonating(false);
        try {
            const res = await api.get("/auth/me");
            setUser(res.data.user);
        } catch (e) {
            /* ignore */
        }
    }, []);

    return (
        <AuthContext.Provider value={{
            user, token, ready, impersonating,
            login, logout, refreshUser, updateUser: setUser,
            impersonate, stopImpersonate,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    return useContext(AuthContext);
}
